import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { getAllDenomination } from '../../../virtco/services/data-provider';
import { logout } from '../../../redux/action/logout-action';

interface Package {
  id: string;
  title: string;
  duration: string | null;
  price: string;
  description: string;
  isFree?: boolean;
}

interface PackagesPricingState {
  packages: Package[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  // Tracks the consultationId we've successfully fetched packages for.
  // Used to skip refetching on step-navigation remounts so that locally-typed
  // (unsaved) edits don't get overwritten by the API response.
  fetchedForId: string | null;
}

const emptyPackage = () => ({
  id: '',
  title: '',
  duration: null,
  price: '',
  description: '',
  isFree: false,
});

const initialState: PackagesPricingState = {
  packages: [emptyPackage()],
  status: 'idle',
  fetchedForId: null,
};

export const fetchPackagesPricing = createAsyncThunk(
  'packagesPricing/fetchPackagesPricing',
  async (consultationId: string) => {
    const res = await getAllDenomination(consultationId);
    const data = res.data.data || [];
    const mappedPackages = data.map((item: any) => ({
      id: item.id,
      title: item.title,
      duration: item.timeInMinutes?.toString() || null,
      price: item.price?.toString() || '',
      description: item.briefDescription || '',
      isFree: item.price === 0,
    }));
    return mappedPackages.length > 0 ? mappedPackages : [emptyPackage()];
  }
);

const packagesPricingSlice = createSlice({
  name: 'packagesPricing',
  initialState,
  reducers: {
    setData(state, action: PayloadAction<Package[]>) {
      state.packages = action.payload.length > 0 ? action.payload : [emptyPackage()];
    },
    clearData(state) {
      state.packages = [emptyPackage()];
      state.status = 'idle';
      state.fetchedForId = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchPackagesPricing.pending, (state, action) => {
        if (state.fetchedForId !== action.meta.arg) {
          state.packages = [emptyPackage()];
          state.fetchedForId = null;
        }
        state.status = 'loading';
      })
      .addCase(fetchPackagesPricing.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.packages = action.payload.length > 0 ? action.payload : [emptyPackage()];
        state.fetchedForId = action.meta.arg;
      })
      .addCase(fetchPackagesPricing.rejected, state => {
        state.status = 'failed';
      })
      .addCase(logout, () => initialState);
  },
});

export const { setData, clearData } = packagesPricingSlice.actions;
export default packagesPricingSlice.reducer; 